
import { useState, useCallback, useRef } from 'react';
import { Edge, Node } from '@xyflow/react';
import { NodeData } from '../initial-nodes';

interface DiagramState {
  nodes: Node<NodeData>[];
  edges: Edge[];
}

interface UndoRedoResult {
  newState: DiagramState;
  newIndex: number;
}

export function useDiagramHistory() {
  const [history, setHistory] = useState<DiagramState[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const isApplyingHistoryRef = useRef(false);

  // Add current diagram state to history
  const addToHistory = useCallback((nodes: Node<NodeData>[], edges: Edge[], ignoreSelf = false) => {
    if (ignoreSelf || isApplyingHistoryRef.current) return;

    setHistory((prev) => {
      // If we're not at the end of history, cut off the future states
      const newHistory = historyIndex < prev.length - 1 
        ? prev.slice(0, historyIndex + 1) 
        : prev;
      
      // Prevent duplicate states - compare with last state
      if (newHistory.length > 0) {
        const lastState = newHistory[newHistory.length - 1];
        const nodesMatch = JSON.stringify(lastState.nodes) === JSON.stringify(nodes);
        const edgesMatch = JSON.stringify(lastState.edges) === JSON.stringify(edges);
        if (nodesMatch && edgesMatch) {
          return prev; // Don't add duplicate
        }
      }
      
      // Add the new state to history
      const nextState = [...newHistory, { nodes, edges }];
      setHistoryIndex(nextState.length - 1);
      return nextState;
    });
  }, [historyIndex]);

  // Handle undo
  const handleUndo = useCallback((): UndoRedoResult | null => {
    if (historyIndex <= 0) return null;
    
    isApplyingHistoryRef.current = true;
    const newIndex = historyIndex - 1;
    const newState = history[newIndex];
    
    setHistoryIndex(newIndex);
    
    // Reset flag after a short delay to ensure state updates complete
    setTimeout(() => {
      isApplyingHistoryRef.current = false;
    }, 100);
    
    return { newState, newIndex };
  }, [history, historyIndex]);

  // Handle redo
  const handleRedo = useCallback((): UndoRedoResult | null => {
    if (historyIndex >= history.length - 1) return null;
    
    isApplyingHistoryRef.current = true;
    const newIndex = historyIndex + 1;
    const newState = history[newIndex];
    
    setHistoryIndex(newIndex);
    
    // Reset flag after a short delay to ensure state updates complete
    setTimeout(() => {
      isApplyingHistoryRef.current = false;
    }, 100);
    
    return { newState, newIndex };
  }, [history, historyIndex]);

  return {
    history,
    historyIndex,
    addToHistory,
    handleUndo,
    handleRedo,
    setHistory,
    setHistoryIndex,
    isApplyingHistoryRef,
  };
}
